import { CANDIDATE_ACTIVITY_QUERY } from "@lib/graphql/queries";
import {
  Bot,
  ClipboardList,
  FileDown,
  FileText,
  GitMerge,
  Mail,
  MessageSquare,
  Send,
  User as UserIcon,
  UserPlus,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "urql";

export type CandidateActivityEventSource = "user" | "ai_agent" | "automated";

export interface CandidateActivityEvent {
  id: string;
  kind: string;
  occurredAt: string;
  summary: string;
  details: Record<string, unknown>;
  source: CandidateActivityEventSource;
  actorLabel: string | null;
  actor: { id: string; name: string } | null;
  campaign: { id: string; title: string } | null;
}

const SOURCE_FILTERS: { value: "all" | CandidateActivityEventSource; label: string }[] = [
  { value: "all", label: "All" },
  { value: "user", label: "People" },
  { value: "ai_agent", label: "AI" },
  { value: "automated", label: "System" },
];

const KIND_FILTERS: { value: string; label: string; kinds: string[] }[] = [
  {
    value: "pipeline",
    label: "Pipeline",
    kinds: [
      "candidate_created",
      "campaign_added",
      "campaign_removed",
      "stage_change",
      "selection_step_added",
      "talent_pool_added",
      "pool_removed",
      "offer",
    ],
  },
  {
    value: "assessment",
    label: "Assessments",
    kinds: ["assessment_version", "assessment_threshold_override"],
  },
  {
    value: "outreach",
    label: "Outreach",
    kinds: ["outreach_attempt", "outreach_message_version"],
  },
  {
    value: "feedback",
    label: "Notes & feedback",
    kinds: ["note", "interview_contribution", "portal_activity"],
  },
  {
    value: "record",
    label: "Record changes",
    kinds: [
      "details_updated",
      "profile_data_updated",
      "resume_attached",
      "resume_detached",
      "candidate_merged",
      "anonymised",
      "document_export",
    ],
  },
];

const PAGE_SIZE = 20;

export function CandidateActivitySection({ candidateId }: { candidateId: string }) {
  const [{ data, fetching }] = useQuery({
    query: CANDIDATE_ACTIVITY_QUERY,
    variables: { id: candidateId },
  });
  const [sourceFilter, setSourceFilter] = useState<"all" | CandidateActivityEventSource>("all");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const events: CandidateActivityEvent[] = data?.candidate?.activityEvents ?? [];
  const kindGroup = KIND_FILTERS.find((group) => group.value === kindFilter);
  const filtered = events.filter(
    (event) =>
      (sourceFilter === "all" || event.source === sourceFilter) &&
      (!kindGroup || kindGroup.kinds.includes(event.kind)),
  );
  const visible = filtered.slice(0, visibleCount);

  return (
    <div className="mt-6">
      <h2 className="text-lg font-medium text-text-primary">Activity</h2>
      <p className="mt-1 text-sm text-text-muted">
        Everything that happened to this candidate, across all campaigns.
      </p>
      {events.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {SOURCE_FILTERS.map((option) => (
            <FilterChip
              key={option.value}
              label={option.label}
              active={sourceFilter === option.value}
              onClick={() => setSourceFilter(option.value)}
            />
          ))}
          <span className="mx-1 h-4 w-px bg-ring" aria-hidden="true" />
          <FilterChip
            label="Everything"
            active={kindFilter === "all"}
            onClick={() => setKindFilter("all")}
          />
          {KIND_FILTERS.map((group) => (
            <FilterChip
              key={group.value}
              label={group.label}
              active={kindFilter === group.value}
              onClick={() => setKindFilter(group.value)}
            />
          ))}
        </div>
      )}
      <div className="mt-3">
        {fetching && events.length === 0 ? (
          <p className="text-sm text-text-muted">Loading activity...</p>
        ) : filtered.length === 0 && events.length > 0 ? (
          <p className="text-sm text-text-muted">No activity matches the selected filters.</p>
        ) : (
          <CandidateActivityTimeline events={visible} />
        )}
        {filtered.length > visibleCount && (
          <button
            type="button"
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
            className="mt-3 rounded-md border border-input-border px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-card-hover"
          >
            Show more ({filtered.length - visibleCount} older)
          </button>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition-colors ${
        active
          ? "bg-accent text-white ring-accent"
          : "bg-card text-text-secondary ring-ring hover:bg-card-hover"
      }`}
    >
      {label}
    </button>
  );
}

export function CandidateActivityTimeline({ events }: { events: CandidateActivityEvent[] }) {
  if (events.length === 0) {
    return <p className="text-sm text-text-muted">No activity recorded yet.</p>;
  }

  return (
    <ol className="relative space-y-4 border-l border-ring pl-6" aria-label="Candidate activity">
      {events.map((event) => (
        <li key={event.id} className="relative">
          <span
            className={`absolute -left-[31px] flex h-6 w-6 items-center justify-center rounded-full ${iconBackground(event)}`}
            aria-hidden="true"
          >
            {iconFor(event)}
          </span>
          <div className="rounded-lg bg-card p-3 shadow-sm ring-1 ring-ring">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-text-primary">{event.summary}</p>
              {event.source === "ai_agent" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  <Bot size={10} /> AI
                </span>
              )}
              {event.campaign && (
                <Link
                  to={`/campaigns/${event.campaign.id}`}
                  className="inline-flex rounded-full bg-card-hover px-2 py-0.5 text-[10px] font-medium text-text-secondary ring-1 ring-ring hover:text-accent"
                >
                  {event.campaign.title}
                </Link>
              )}
            </div>
            <p className="mt-0.5 text-xs text-text-muted">
              {actorName(event)}
              {" · "}
              {formatTimestamp(event.occurredAt)}
            </p>
            {renderExtras(event)}
          </div>
        </li>
      ))}
    </ol>
  );
}

function actorName(event: CandidateActivityEvent): string {
  if (event.actor) return event.actor.name;
  if (event.actorLabel) return event.actorLabel;
  return event.source === "ai_agent" ? "AI assistant" : "System";
}

function iconFor(event: CandidateActivityEvent): ReactNode {
  if (event.source === "ai_agent") return <Bot size={12} />;
  switch (event.kind) {
    case "candidate_created":
    case "campaign_added":
    case "talent_pool_added":
      return <UserPlus size={12} />;
    case "stage_change":
      return <UserIcon size={12} />;
    case "assessment_version":
    case "assessment_threshold_override":
      return <ClipboardList size={12} />;
    // The paper plane is reserved for logged sends; drafts are documents (#3169).
    case "outreach_attempt":
      return <Send size={12} />;
    case "outreach_message_version":
      return <FileText size={12} />;
    case "note":
      return <MessageSquare size={12} />;
    case "interview_contribution":
    case "selection_step_added":
      return <Users size={12} />;
    case "offer":
      return <Mail size={12} />;
    case "candidate_merged":
      return <GitMerge size={12} />;
    case "document_export":
      return <FileDown size={12} />;
    default:
      return <FileText size={12} />;
  }
}

function iconBackground(event: CandidateActivityEvent): string {
  if (event.source === "ai_agent") return "bg-blue-50 text-blue-700";
  switch (event.kind) {
    case "candidate_created":
    case "campaign_added":
    case "talent_pool_added":
      return "bg-emerald-50 text-emerald-700";
    case "stage_change":
      return "bg-accent-light text-amber-800";
    case "assessment_version":
    case "assessment_threshold_override":
      return "bg-violet-50 text-violet-700";
    case "outreach_attempt":
    case "outreach_message_version":
      return "bg-sky-50 text-sky-700";
    case "offer":
      return "bg-amber-50 text-amber-700";
    case "candidate_merged":
      return "bg-red-50 text-red-700";
    default:
      return "bg-card-hover text-text-secondary";
  }
}

function renderExtras(event: CandidateActivityEvent): ReactNode {
  const deepLink = event.details.deep_link as { path?: string; label?: string } | null | undefined;
  if (deepLink && typeof deepLink.path === "string") {
    return (
      <p className="mt-1 text-xs">
        <Link to={deepLink.path} className="text-accent hover:underline">
          {deepLink.label ?? "Open"}
        </Link>
      </p>
    );
  }

  const reason = event.details.reason;
  if (typeof reason === "string" && reason.trim().length > 0) {
    return <p className="mt-1 text-xs italic text-text-muted">"{reason}"</p>;
  }

  const changes = event.details.changes;
  if (changes && typeof changes === "object") {
    return (
      <ul className="mt-1 space-y-0.5 text-xs text-text-muted">
        {Object.entries(changes as Record<string, [unknown, unknown]>).map(([field, change]) => (
          <li key={field}>
            <span className="font-medium">{fieldLabel(field)}:</span>{" "}
            <span className="line-through">{formatValue(change?.[0])}</span>
            {" → "}
            <span className="text-text-secondary">{formatValue(change?.[1])}</span>
          </li>
        ))}
      </ul>
    );
  }

  const fields = event.details.fields;
  if (Array.isArray(fields) && fields.length > 0) {
    return <p className="mt-1 text-xs text-text-muted">Changed: {fields.join(", ")}</p>;
  }

  const filename = event.details.filename;
  if (typeof filename === "string" && filename.length > 0) {
    return <p className="mt-1 text-xs text-text-muted">{filename}</p>;
  }

  return null;
}

function fieldLabel(field: string): string {
  const label = field.replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(empty)";
  return String(value);
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
